import React, { useEffect } from 'react';
import './privacy.css';

const PrivacyPolicy = () => {
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const node = document.getElementById(id);
      if (node) {
        requestAnimationFrame(() => {
          node.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  }, []);

  return (
    <div className="kw__privacy-page">
      <div className="kw__privacy-container">
        <header className="kw__privacy-header">
          <p className="kw__privacy-kicker">KOTHAWALA GROUP INC</p>
          <h1>Legal & Privacy Center</h1>
          <p className="kw__privacy-effective"><strong>Effective Date / Last Updated:</strong> September 11, 2026</p>
        </header>

        <section id="sms-terms" className="kw__privacy-section">
          <h2>SMS Terms & Conditions</h2>

          <h3>Company / Brand</h3>
          <p>The SMS messaging program is operated by <strong>KOTHAWALA GROUP INC</strong> under the brand name <strong>Kothawala Group</strong>.</p>

          <h3>Purpose of SMS Program</h3>
          <p>The SMS program is for individuals who apply for employment opportunities with Kothawala Group.</p>
          <p>SMS messages may include application receipt confirmations, requests to complete employment application information, interview scheduling, interview confirmations, interview reminders, recruitment updates, onboarding instructions, responses to applicant questions, and other transactional communications directly related to an individual&apos;s employment application or recruiting process with Kothawala Group.</p>

          <h3>Consent to Receive SMS</h3>
          <p>By voluntarily opting in, the individual agrees to receive SMS/text messages from Kothawala Group at the mobile number they provided.</p>
          <p><strong>Consent to receive SMS messages is not a condition of employment</strong> and is not required to submit an employment application.</p>

          <h3>Message Frequency</h3>
          <p><strong>Message frequency varies.</strong></p>

          <h3>Message and Data Charges</h3>
          <p><strong>Message and data rates may apply.</strong> Your wireless carrier may charge messaging or data fees depending on your mobile plan.</p>

          <h3>Opt-Out / STOP</h3>
          <p><strong>You may opt out of receiving SMS messages at any time by replying STOP to any message.</strong> After opt-out, Kothawala Group will stop campaign messaging to that number, except for an appropriate confirmation that the opt-out request has been processed.</p>

          <h3>HELP / Customer Support</h3>
          <p><strong>Reply HELP for assistance.</strong></p>
          <p>
            <strong>Email:</strong> <a href="mailto:admin@kothawalagroup.com">admin@kothawalagroup.com</a>
          </p>
          <p>Support phone details will be published after confirmation of the correct monitored number.</p>

          <h3>Supported Carriers / Delivery</h3>
          <p>Message delivery is subject to effective transmission by your wireless carrier and network operator. Kothawala Group is not responsible for delayed or undelivered messages caused by telecommunications providers or other factors outside its reasonable control.</p>

          <h3>Eligibility</h3>
          <p>By providing a mobile number, you represent that you are authorized to use and control that number and to consent to receive messages at that number.</p>

          <h3>Changing Mobile Numbers</h3>
          <p>If you stop using or change your mobile number, please notify Kothawala Group promptly so we can update communication preferences and records.</p>

          <h3>Privacy</h3>
          <p>
            Please review our <a href="#privacy-policy">Privacy Policy</a> for details about how personal information is collected, used, and protected.
          </p>

          <h3>Changes to SMS Terms</h3>
          <p>Kothawala Group may update these SMS Terms & Conditions from time to time. Any updates will be posted on this page with a revised effective date.</p>
        </section>

        <section id="privacy-policy" className="kw__privacy-section">
          <h2>Privacy Policy</h2>
          <p>This Privacy Policy explains what information Kothawala Group may collect, why it is collected, how it is used, when it may be disclosed, and how it is protected.</p>

          <h3>Information We May Collect</h3>
          <p>Depending on how you interact with our website or apply for employment, we may collect:</p>
          <ul>
            <li>Name</li>
            <li>Telephone/mobile number</li>
            <li>Email address</li>
            <li>Employment application information</li>
            <li>Resume or employment history submitted by the applicant</li>
            <li>Communications with Kothawala Group</li>
            <li>SMS opt-in status and SMS opt-out status</li>
            <li>Technical website information where applicable, such as browser type, device information, IP address, and usage logs</li>
          </ul>

          <h3>How Information Is Used</h3>
          <p>Information may be used to:</p>
          <ul>
            <li>Process employment applications</li>
            <li>Communicate with candidates</li>
            <li>Schedule and confirm interviews</li>
            <li>Provide application and recruitment updates</li>
            <li>Respond to questions</li>
            <li>Provide onboarding-related information where applicable</li>
            <li>Maintain consent and opt-out records</li>
            <li>Operate and secure the website</li>
            <li>Comply with applicable legal and compliance requirements</li>
            <li>Prevent fraud, abuse, or misuse</li>
            <li>Provide the services requested by users</li>
          </ul>

          <h3>SMS Privacy / Mobile Information</h3>
          <p className="kw__privacy-highlight">No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. SMS opt-in data and consent will not be sold, rented, or shared with third parties for their own marketing or promotional purposes.</p>
          <p>We may disclose information, including mobile information and SMS consent status, to service providers that assist us in operating our communications and messaging services, such as messaging platform providers, telecommunications carriers, hosting providers, and other vendors acting on our behalf. Such information is provided only as necessary to perform services for Kothawala Group and is not provided to those parties for their own marketing or promotional purposes.</p>
          <p className="kw__privacy-highlight">SMS opt-in consent is specific to Kothawala Group and will not be sold, rented, transferred, or provided to another company for that company&apos;s independent SMS marketing program.</p>

          <h3>How Information May Be Disclosed</h3>
          <p>We may disclose information to service providers operating on our behalf, when required by law, to protect rights and safety, or in connection with a corporate transaction subject to applicable confidentiality and legal safeguards.</p>

          <h3>Information Security</h3>
          <p>Kothawala Group uses reasonable administrative, technical, and organizational safeguards designed to protect personal information against unauthorized access, disclosure, alteration, or destruction. No system can be guaranteed completely secure.</p>

          <h3>Data Retention</h3>
          <p>We retain personal information for as long as reasonably necessary for the purposes described in this policy, to maintain consent and opt-out records, to meet legitimate business needs, and to satisfy legal or compliance obligations.</p>

          <h3>Third-Party Websites</h3>
          <p>Our website may contain links to third-party websites. Those websites operate under their own privacy notices and practices, and Kothawala Group is not responsible for the content or practices of third-party sites.</p>

          <h3>Contact Information</h3>
          <p><strong>KOTHAWALA GROUP INC</strong><br />
            <strong>Kothawala Group</strong><br />
            Houston, Texas<br />
            <strong>Email:</strong> <a href="mailto:admin@kothawalagroup.com">admin@kothawalagroup.com</a>
          </p>
          <p>Additional mailing address and phone details will be added after confirmation of the correct monitored contact channels.</p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
